import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { founder } from '../../data/content';
import { ease } from '../../core/format/motion';
import { chipCls } from '../../ui/styles';
import type { Product, Routine } from '../../core/types/catalog';
type Section = {
  id: string;
  title: string;
  body: ReactNode;
};

/** Uitklapblokken onder de knop: waarom, gebruik, ingrediënten en huidtype. Eén tegelijk open. */
export function ProductDetails({
  product,
  inRoutines
}: {
  product: Product;
  inRoutines: Routine[];
}) {
  const [open, setOpen] = useState('why');
  const reduce = useReducedMotion();

  // Ander product: weer beginnen bij "waarom".
  useEffect(() => setOpen('why'), [product.id]);
  const sections: Section[] = [{
    id: 'why',
    title: `Waarom ${founder.firstName} dit kiest`,
    body: product.why
  }, {
    id: 'how',
    title: 'Zo gebruik je het',
    body: product.howTo
  }, {
    id: 'ingredients',
    title: 'Werkzame ingrediënten',
    body: <ul className="flex flex-wrap gap-2" data-id="element-8094" data-mp="xrsqevjm8qrt">
          {product.keyIngredients.map(i => <li key={i} className="rounded-full bg-blush px-3.5 py-1.5 text-[14px] text-ink" data-id="element-8095" data-mp="1brbyjocfg244">
              {i}
            </li>)}
        </ul>
  }, {
    id: 'skin',
    title: 'Past bij',
    body: <div className="space-y-4" data-id="element-8096" data-mp="jz33mow9jhtn">
          <ul className="flex flex-wrap gap-2" data-id="element-8097" data-mp="f2cllw0wec5l">
            {product.skinTypes.map(s => <li key={s} data-id="element-8098" data-mp="st94gv50vezg">
                <Link to={`/webshop?huid=${encodeURIComponent(s)}`} className={`${chipCls(false)} inline-flex items-center`} data-id="element-8099" data-mp="suj3pnnm2t7o">
                  {s}
                </Link>
              </li>)}
          </ul>
          {inRoutines.length > 0 && <p data-id="element-8100" data-mp="3sg7ph7zou93x">
              Zit in{' '}
              {inRoutines.map((r, i) => <Fragment key={r.id} data-id="element-8101" data-mp="1vfbkyk2y2jxa" data-mpi={i}>
                  {i > 0 && (i === inRoutines.length - 1 ? ' en ' : ', ')}
                  <Link to="/routines" className="text-ink underline underline-offset-4" data-id="element-8102" data-mp="20e4xa96hw4vv">
                    {r.name}
                  </Link>
                </Fragment>)}
              .
            </p>}
          <p data-id="element-8103" data-mp="2a7bz5k2112r7">Twijfel je? Stuur {founder.firstName} een appje met een foto — ze kijkt persoonlijk mee.</p>
        </div>
  }];
  return <div className="mt-8 divide-y divide-line border-y border-line" data-id="element-8104" data-mp="1hfsdlthyjcm1">
      {sections.map(s => {
      const isOpen = open === s.id;
      const panelId = `product-${s.id}`;
      return <div key={s.id} data-id="element-8105" data-mp="1rf1ez7f5jtif">
            <h2 data-id="element-8106" data-mp="1rgcjj9gf95al">
              <button type="button" aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpen(isOpen ? '' : s.id)} className="flex w-full items-center justify-between py-4 text-left text-[15px] text-ink" data-id="element-8107" data-mp="1rcona2jle4s6">
                {s.title}
                <ChevronDownIcon className={`h-4 w-4 text-muted transition-transform duration-200 ease-soft ${isOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-8108" data-mp="m6yixapts4kj" />
              </button>
            </h2>
            <AnimatePresence initial={false} data-id="element-8109" data-mp="7ilssudf5fxy">
              {isOpen && <motion.div id={panelId} initial={reduce ? {
            opacity: 0
          } : {
            height: 0,
            opacity: 0
          }} animate={reduce ? {
            opacity: 1
          } : {
            height: 'auto',
            opacity: 1
          }} exit={reduce ? {
            opacity: 0
          } : {
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.22,
            ease
          }} className="overflow-hidden" data-id="element-8110" data-mp="7ghj1vsu9mpr">
                  <div className="pb-5 text-[15px] leading-relaxed text-muted" data-id="element-8111" data-mp="1nwu4ftwni4ut">{s.body}</div>
                </motion.div>}
            </AnimatePresence>
          </div>;
    })}
    </div>;
}